// SelectedClientWork — quiet credibility strip used on /reviews and /business.
//
// Phase 9 social-proof addition. Names a small set of organizations the
// studio has photographed for, with restrained treatment so the strip
// supports conversion without competing with bundles or testimonials.
//
// Rendering:
//   - Each tile is a uniform editorial card (porcelain bg, hairline
//     border, even padding).
//   - If `logo` URL is provided in the brand object: <img> rendered with
//     `filter: grayscale(100%) opacity(0.7)` so brand colors don't shout
//     and the strip reads as one cohesive layout.
//   - If `logo` absent: typographic wordmark — same serif lockup as the
//     editorial type elsewhere on the site. No fake logos, no AI mocks.
//
// Layout:
//   - mobile (<640px):  grid-cols-2 with gap-3
//   - desktop (≥768px): grid-cols-4 with gap-4
//
// Per Phase 9 spec: do NOT pair this section with "Trusted by" framing or
// any endorsement claim. The honest read is "selected client work" — the
// studio has done work for these organizations.

import React from 'react';
import {cn} from '@/lib/utils';
import {Section, Kicker, SerifTitle} from '@/components/Section';
import {SELECTED_CLIENTS} from '@/lib/data';

export const SelectedClientWork = ({
    brands = SELECTED_CLIENTS,
    spacing = 'normal',
    bordered = true,
    variant = 'default',
    testId = 'selected-client-work',
}) => {
    if (!brands || brands.length === 0) return null;

    // Phase 10: `variant="compact"` tightens the strip for /reviews where
    // it sits below the testimonials grid as a credibility cap (not the
    // page-lead emphasis it has on /business). Compact mode reduces:
    //   - header → grid gap (mb-8 → mb-6)
    //   - tile vertical padding (py-5/6 → py-4/5)
    //   - tile min-height (80/96 → 64/76 px)
    //   - tile typographic scale (15/17 → 14/15 px)
    // Wordmark/logo treatment unchanged.
    const isCompact = variant === 'compact';

    const tileClasses = cn(
        'flex items-center justify-center',
        isCompact
            ? 'min-h-[64px] sm:min-h-[76px] px-3 sm:px-5 py-4 sm:py-5'
            : 'min-h-[80px] sm:min-h-[96px] px-4 sm:px-6 py-5 sm:py-6',
        'bg-background border border-border rounded-[2px]',
        'transition-colors',
    );

    const wordmarkClasses = cn(
        'font-serif-display text-center leading-tight text-foreground/70 tracking-[0.005em]',
        isCompact
            ? 'text-[14px] sm:text-[15px]'
            : 'text-[15px] sm:text-[17px]',
    );

    const logoMaxHeightClass = isCompact
        ? 'max-h-[32px] sm:max-h-[40px]'
        : 'max-h-[40px] sm:max-h-[48px]';

    return (
        <section
            data-testid={testId}
            data-variant={variant}
            className={cn(bordered && 'border-t border-border')}
        >
            <Section spacing={spacing}>
                <div className={cn(
                    'grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end',
                    isCompact ? 'mb-5 sm:mb-6' : 'mb-7 sm:mb-9',
                )}>
                    <div className="lg:col-span-7">
                        <Kicker>Selected client work</Kicker>
                        <SerifTitle className="mt-3">
                            A few of the brands, teams, and clients who
                            trusted the process.
                        </SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                        Real organizations, real client work, and enough range to
                        make the next inquiry feel lower&#8209;risk.
                    </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                    {brands.map((b) => (
                        <div
                            key={b.id}
                            data-testid={`client-tile-${b.id}`}
                            className={tileClasses}
                        >
                            {b.logo ? (
                                <img
                                    src={b.logo}
                                    alt={b.name}
                                    loading="lazy"
                                    decoding="async"
                                    className={cn(logoMaxHeightClass, 'w-auto object-contain')}
                                    style={{filter: 'grayscale(100%)', opacity: 0.7}}
                                />
                            ) : (
                                <span className={wordmarkClasses}>
                                    {b.name}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </Section>
        </section>
    );
};

export default SelectedClientWork;
